import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'

import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import CartButton from '../components/CartButton'
import TripChatMobileTabs, { type TripChatMobileTabId, type TripChatMobileTabItem } from './TripChatMobileTabs'

interface TripChatMobileAppShellProps {
  children?: ReactNode
  onMenuOpen?: () => void
  tabPanels?: Partial<Record<TripChatMobileTabId, ReactNode>>
  showComposerReservedSpace?: boolean
  showCartButton?: boolean
  isCartButtonDisabled?: boolean
  cartItemCount?: number
  cartTotalPrice?: number
  isCartButtonLoading?: boolean
}

interface TripChatMobileAppShellContextValue {
  activeTab: TripChatMobileTabId
  setActiveTab: (tabId: TripChatMobileTabId) => void
}

const TripChatMobileAppShellContext = createContext<TripChatMobileAppShellContextValue | undefined>(undefined)

const ShellRoot = styled.div`
  display: flex;
  height: 100vh;
  height: 100dvh;
  flex-direction: column;
  overflow: hidden;
  padding-top: env(safe-area-inset-top);
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const ShellHeader = styled.header`
  display: grid;
  grid-template-columns: ${rem(52)} minmax(0, 1fr) auto;
  align-items: center;
  min-height: ${rem(54)};
  padding: 0 ${rem(4)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const HeaderSpacer = styled.span`
  width: ${rem(44)};
  height: ${rem(44)};
`

const IconButton = styled.button`
  display: inline-flex;
  width: ${rem(44)};
  height: ${rem(44)};
  align-items: center;
  justify-content: center;
  justify-self: center;
  border: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  cursor: pointer;

  &:disabled {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    cursor: default;
  }

  &:focus-visible {
    outline: ${rem(2)} solid ${props => props.theme.palette.highlight.primary.normalForeground};
    outline-offset: ${rem(2)};
  }
`

const ShellBody = styled.main`
  position: relative;
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const TabPanels = styled.div`
  position: relative;
  min-height: 0;
  flex: 1 1 auto;
  overflow: hidden;
`

const TabPanel = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: ${rem(20)};

  &.custom {
    overflow: auto;
    padding: 0;
  }

  &[hidden] {
    display: none;
  }
`

const PlaceholderView = styled.div`
  display: flex;
  min-height: 100%;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(8)};
  text-align: center;
`

const ComposerReservedSpace = styled.footer`
  min-height: calc(${rem(68)} + env(safe-area-inset-bottom));
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const DEFAULT_TAB_PANELS: Partial<Record<TripChatMobileTabId, ReactNode>> = {}

interface TripChatMobileTabPanelProps {
  activeTab: TripChatMobileTabId
  labels: ReturnType<typeof useTripChatMobileLabels>
  tab: TripChatMobileTabItem
  tabPanels: Partial<Record<TripChatMobileTabId, ReactNode>>
}

function TripChatMobileTabPanel({ activeTab, labels, tab, tabPanels }: TripChatMobileTabPanelProps) {
  const panelContent = tabPanels[tab.id]
  const hasPanelContent = panelContent !== undefined

  return (
    <TabPanel
      className={hasPanelContent ? 'custom' : undefined}
      data-testid={`trip-chat-mobile-tab-panel-${tab.id}`}
      hidden={activeTab !== tab.id}
    >
      {hasPanelContent && panelContent}
      {!hasPanelContent && (
        <PlaceholderView>
          <Heading variant="heading5" as="h2">
            {tab.label}
          </Heading>
          <BodyText variant="medium">
            {labels.placeholders[tab.id]}
          </BodyText>
        </PlaceholderView>
      )}
    </TabPanel>
  )
}

function useTripChatMobileLabels() {
  const intl = useIntl()

  return useMemo(() => ({
    menuButton: intl.formatMessage({
      id: 'Rh5EB1l_8dTFdLNrgEnU',
      defaultMessage: 'Open trip menu',
      description: 'Accessibility label for the mobile trip planner menu button in the app shell header',
    }),
    cartButton: intl.formatMessage({
      id: 'gUgzcjMkoSgOtM_qF0I3',
      defaultMessage: 'Open trip cart',
      description: 'Accessibility label for the mobile trip cart button in the app shell header',
    }),
    tabList: intl.formatMessage({
      id: 'HwL8PoaVwNML5Nh72udF',
      defaultMessage: 'Trip planner mobile views',
      description: 'Accessibility label for the mobile trip planner tab navigation control',
    }),
    tabs: {
      chat: intl.formatMessage({
        id: 'W6AYq4RaOfBu_SyMN7Vv',
        defaultMessage: 'Chat',
        description: 'Tab label for the mobile AI TripPlanner chat conversation view',
      }),
      timeline: intl.formatMessage({
        id: 'Ubu2tgXvGlszkJ3USdI-',
        defaultMessage: 'Timeline',
        description: 'Tab label for the mobile AI TripPlanner timeline view',
      }),
      list: intl.formatMessage({
        id: 'v3ioTc-88OqTbL9bnkQA',
        defaultMessage: 'List',
        description: 'Tab label for the mobile AI TripPlanner itinerary list view',
      }),
      map: intl.formatMessage({
        id: 'IASdasfMP-rhaQoHcb5w',
        defaultMessage: 'Map',
        description: 'Tab label for the mobile AI TripPlanner trip map view',
      }),
    },
    placeholders: {
      chat: intl.formatMessage({
        id: 'EwQ3FblF7olWsyrdOhnq',
        defaultMessage: 'Your planning chat will appear here.',
        description: 'Passive placeholder text shown before the mobile chat view is wired',
      }),
      timeline: intl.formatMessage({
        id: 'KGa4wVpkemPhqPRpVEcK',
        defaultMessage: 'Your trip timeline will appear here.',
        description: 'Passive placeholder text shown before the mobile timeline view is wired',
      }),
      list: intl.formatMessage({
        id: '1mQR6iQWYoQO8jSRvX6z',
        defaultMessage: 'Your trip list will appear here.',
        description: 'Passive placeholder text shown before the mobile list view is wired',
      }),
      map: intl.formatMessage({
        id: '_zTqwDdDiH4vhnUY86fd',
        defaultMessage: 'Your trip map will appear here.',
        description: 'Passive placeholder text shown before the mobile map view is wired',
      }),
    },
  }), [intl])
}

function TripChatMobileAppShell({
  children,
  onMenuOpen,
  tabPanels = DEFAULT_TAB_PANELS,
  showComposerReservedSpace = true,
  showCartButton = false,
  isCartButtonDisabled = false,
  cartItemCount = 0,
  cartTotalPrice = 0,
  isCartButtonLoading = false,
}: TripChatMobileAppShellProps) {
  const labels = useTripChatMobileLabels()
  const eventBus = useTripPlannerEventBus()
  const [activeTab, setActiveTab] = useState<TripChatMobileTabId>('chat')

  const tabs = useMemo<ReadonlyArray<TripChatMobileTabItem>>(() => [
    { id: 'chat', label: labels.tabs.chat },
    { id: 'timeline', label: labels.tabs.timeline },
    { id: 'list', label: labels.tabs.list },
    { id: 'map', label: labels.tabs.map },
  ], [labels.tabs])

  const contextValue = useMemo<TripChatMobileAppShellContextValue>(() => ({
    activeTab,
    setActiveTab,
  }), [activeTab])

  const handleCartClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const tabPanelElements = useMemo(() => tabs.map(tab => (
    <TripChatMobileTabPanel
      key={tab.id}
      activeTab={activeTab}
      labels={labels}
      tab={tab}
      tabPanels={tabPanels}
    />
  )), [activeTab, labels, tabPanels, tabs])

  return (
    <TripChatMobileAppShellContext.Provider value={contextValue}>
      <ShellRoot data-testid="trip-chat-mobile-app-shell">
        <ShellHeader>
          {onMenuOpen && (
            <IconButton
              type="button"
              aria-label={labels.menuButton}
              onClick={onMenuOpen}
            >
              <LineBarsIcon size="S" />
            </IconButton>
          )}
          {!onMenuOpen && <HeaderSpacer aria-hidden="true" />}
          <HeaderSpacer aria-hidden="true" />
          {showCartButton && !isCartButtonDisabled && (
            <CartButton
              itemCount={cartItemCount}
              totalPrice={cartTotalPrice}
              isLoading={isCartButtonLoading}
              onClick={handleCartClick}
            />
          )}
          {showCartButton && isCartButtonDisabled && (
            <IconButton
              type="button"
              aria-label={labels.cartButton}
              disabled={isCartButtonDisabled}
              onClick={handleCartClick}
            >
              <LineShoppingCartIcon size="S" />
            </IconButton>
          )}
          {!showCartButton && <HeaderSpacer aria-hidden="true" />}
        </ShellHeader>
        <TripChatMobileTabs
          activeTab={activeTab}
          ariaLabel={labels.tabList}
          tabs={tabs}
          onTabChange={setActiveTab}
        />
        <ShellBody>
          <TabPanels>
            {tabPanelElements}
          </TabPanels>
          {showComposerReservedSpace && (
            <ComposerReservedSpace
              aria-hidden="true"
              data-testid="trip-chat-mobile-composer-reserved-space"
            />
          )}
        </ShellBody>
        {children}
      </ShellRoot>
    </TripChatMobileAppShellContext.Provider>
  )
}

export function useTripChatMobileAppShell() {
  const context = useContext(TripChatMobileAppShellContext)

  if (!context) {
    throw new Error('useTripChatMobileAppShell must be used within TripChatMobileAppShell')
  }

  return context
}

export default TripChatMobileAppShell
